export interface UseKftLocalStorageOptions<T> {
  /** Controls what is written to localStorage; defaults to the full state value. */
  getPersistableSnapshot?: (value: T) => unknown
}

export const useKftLocalStorage = <T>(
  key: string,
  defaultValue: T,
  options?: UseKftLocalStorageOptions<T>
) => {
  const data = useState(key, () => defaultValue)
  const loaded = ref(false)

  // Function to force load data from localStorage
  const loadFromStorage = () => {
    if (!import.meta.client || loaded.value) return

    let storedData
    try {
      storedData = localStorage.getItem(key)
    } catch (error) {
      console.error('Error accessing local storage', error)
    }
    if (storedData) {
      data.value = JSON.parse(storedData)
    }
    loaded.value = true
  }

  onBeforeMount(() => {
    loadFromStorage()
  })

  watch(
    data,
    () => {
      try {
        const snapshot = options?.getPersistableSnapshot
          ? options.getPersistableSnapshot(data.value)
          : data.value
        localStorage.setItem(key, JSON.stringify(snapshot))
      } catch (error) {
        console.error('Error accessing local storage', error)
      }
    },
    { deep: true }
  )

  return { data, loaded, loadFromStorage }
}
